import axios from 'axios'
import db from 'services/firebase'
import {
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from '@firebase/firestore'
import {
  dismissLoadingToast,
  showErrorToast,
  showLoadingToast,
  showSuccessToast,
  updateLoadingToast,
} from 'app/components/ShowToast'
interface LinearError {
  response?: {
    status?: number
    data?: {
      message?: string
    }
  }
  message?: string
}

interface UserMFAResponse {
  success: boolean
  users?: {id: string; name: string; email: string; mfaEnabled: boolean | null}[]
  error?: string
}

async function createLinearOAuthInstance(accessToken: string) {
  return axios.create({
    baseURL: 'https://api.linear.app/graphql',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })
}

async function refreshLinearAccessToken(refreshToken: string): Promise<string | null> {
  try {
    const response = await fetch(
      'https://us-central1-slate-134a0.cloudfunctions.net/refreshLinearToken',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      }
    )

    if (!response.ok) {
      throw new Error(`Server returned ${response.status}: ${await response.text()}`)
    }

    const { access_token, refresh_token: new_refresh_token, expires_in } = await response.json()

    const tokenData = {
      access_token,
      refresh_token: new_refresh_token,
      expires_at: Date.now() + (expires_in * 1000)
    }
    localStorage.setItem('linear_tokens', JSON.stringify(tokenData))
    localStorage.setItem('linear_access_token', access_token)
    
    return access_token
  } catch (error) {
    console.error('Failed to refresh Linear token:', error)
    return null
  }
}

async function getValidLinearToken(): Promise<string | null> {
  const tokenDataStr = localStorage.getItem('linear_tokens')
  if (!tokenDataStr) {
    return localStorage.getItem('linear_access_token')
  }

  try {
    const tokenData = JSON.parse(tokenDataStr)
    const now = Date.now()
    
    if (tokenData.expires_at && now >= (tokenData.expires_at - 5 * 60 * 1000)) {
      if (tokenData.refresh_token) {
        const newToken = await refreshLinearAccessToken(tokenData.refresh_token)
        return newToken
      }
    }
    
    return tokenData.access_token
  } catch (error) {
    console.error('Error parsing token data:', error)
    return localStorage.getItem('linear_access_token') 
  }
}

async function fetchTeamIdByName(accessToken: string, teamName: string) {
  const validToken = await getValidLinearToken() || accessToken
  const axiosInstance = await createLinearOAuthInstance(validToken)

  const query = `
    query Teams {
      teams {
        nodes {
          id
          name
        }
      }
    }
  `

  try {
    const response = await axiosInstance.post('', {query})
    const teams = response.data.data.teams.nodes
    const foundTeam = teams.find((team) => team.name === teamName)

    if (foundTeam) {
      return foundTeam.id
    } else {
      return null
    }
  } catch (error) {
    return null
  }
}

async function findUserIdByEmail(accessToken: string, email: string) {
  const validToken = await getValidLinearToken() || accessToken
  const axiosInstance = await createLinearOAuthInstance(validToken)

  const query = `
    query Users {
      users {
        nodes {
          id
          name
          email
        }
      }
    }
  `

  try {
    const response = await axiosInstance.post('', {query})
    const users = response.data.data.users.nodes

    const foundUser = users.find((user) => user.email.toLowerCase() === email.toLowerCase())

    if (foundUser) {
      return foundUser.id
    } else {
      return null
    }
  } catch (error) {
    return null
  }
}

async function fetchWorkflowStates(accessToken: string, teamId: string) {
  const validToken = await getValidLinearToken() || accessToken
  const axiosInstance = await createLinearOAuthInstance(validToken)

  const query = `
    query {
      workflowStates(filter: { team: { id: { eq: "${teamId}" } } }) {
        nodes {
          id
          name
          type
        }
      }
    }
  `

  try {
    const response = await axiosInstance.post('', {query})
    const states = response.data.data.workflowStates.nodes

    const todoState = states.find((state) => state.type === 'unstarted')

    if (todoState) {
      return todoState.id
    } else {
      return null
    }
  } catch (error) {
    return null
  }
}

async function createTask(
  accessToken: string,
  teamName: string,
  title: string,
  description: string,
  priority: string,
  dueDate: string,
  assigneeEmail?: string
) {
  try {
    const teamId = await fetchTeamIdByName(accessToken, teamName)
    const todoStatusId = await fetchWorkflowStates(accessToken, teamId)

    if (!todoStatusId) {
      return null
    }

    let assigneeId = null
    if (assigneeEmail) {
      assigneeId = await findUserIdByEmail(accessToken, assigneeEmail)
    }

    const priorityMap = {
      Urgent: 1,
      High: 2,
      Medium: 3,
      Low: 4,
    }
    const priorityLevel = priorityMap[priority]

    const axiosInstance = await createLinearOAuthInstance(accessToken)

    const mutation = `
    mutation {
      issueCreate(input: { 
        teamId: "${teamId}",
        title: "${title}",
        description: "${description}",
        priority: ${priorityLevel}, 
        dueDate: "${dueDate}",
        stateId: "${todoStatusId}"
        ${assigneeId ? `, assigneeId: "${assigneeId}"` : ''}
      }) {
        success
        issue {
          id
          title
          description
          priority
          dueDate
          assignee {
            id
            name
            email
          }
          state {
            id
            name
          }
        }
      }
    }
  `

    const response = await axiosInstance.post('', {query: mutation})

    if (
      response.data.data &&
      response.data.data.issueCreate &&
      response.data.data.issueCreate.success
    ) {
      return response.data.data.issueCreate.issue
    } else {
      return null
    }
  } catch (error) {
    return null
  }
}

async function fetchTickets(accessToken: string, teamName: string) {
  try {
    const validToken = await getValidLinearToken() || accessToken
    const axiosInstance = await createLinearOAuthInstance(validToken)

    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
    const thirtyDaysAgoISO = thirtyDaysAgo.toISOString()

    const query = `
      query {
        issues(filter: { 
          team: { name: { eq: "${teamName}" } }, 
          createdAt: { gte: "${thirtyDaysAgoISO}" } 
        }) {
          nodes {
            id
            title
            assignee {
              id
              name
            }
            createdAt
            description
          }
        }
      }
    `

    const response = await axiosInstance.post('', {query})

    if (response.data.data && response.data.data.issues) {
      return response.data.data.issues.nodes
    } else {
      return []
    }
  } catch (error) {
    return []
  }
}

async function saveTeamMembers(
  accessToken: string,
  teamId: string,
  teamName: string,
  tenantId: string,
  currentUser: any
): Promise<void> {
  if (!teamId || typeof teamId !== 'string') {
    throw new Error('Invalid or missing teamId')
  }

  try {
    const validToken = await getValidLinearToken() || accessToken
    const axiosInstance = await createLinearOAuthInstance(validToken)

    const query = `
      query TeamMembers($teamId: String!) {
        team(id: $teamId) {
          members {
            nodes {
              id
              name
              displayName
              email
            }
          }
        }
      }
    `

    const response = await axiosInstance.post('', {
      query,
      variables: {teamId},
    })

    if (response.data.errors) {
      const errorMessages = response.data.errors.map((err: any) => err.message).join('; ')
      throw new Error(`GraphQL error: ${errorMessages}`)
    }

    const members = response.data.data?.team?.members?.nodes || []

    if (!response.data.data?.team) {
      throw new Error(`Team with ID ${teamId} not found or inaccessible`)
    }

    const membersMap: {[key: string]: any} = {}

    for (const member of members) {
      if (member.id) {
        membersMap[member.id] = {
          platform: 'Linear',
          username: member.email, 
          name: member.displayName || member.name || member.email, 
          userId: member.id, 
          mfaEnabled: null, 
          status: 'active', 
          team: {
            id: teamId,
            name: teamName,
          },
          tenantId,
          uid: currentUser?.uid || '',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }
      }
    }

    const accessReviewRef = doc(db, 'tenants', tenantId, 'access_review', 'linear')

    const linearDoc = await getDoc(accessReviewRef)

    if (linearDoc.exists()) {
      const existingData = linearDoc.data()
      const existingTeams = existingData?.teams || []
      const updatedTeams = [
        ...existingTeams.filter((team: any) => team.teamId !== teamId),
        {teamId, teamName},
      ]
      await updateDoc(accessReviewRef, {
        members: {
          ...existingData.members,
          ...membersMap,
        },
        teams: updatedTeams,
        updatedAt: serverTimestamp(),
      })
    } else {
      await setDoc(accessReviewRef, {
        platform: 'Linear',
        members: membersMap,
        teams: [{teamId, teamName}],
        tenantId,
        uid: currentUser?.uid || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.errors
      ? error.response.data.errors.map((err: any) => err.message).join('; ')
      : error.message || 'An unexpected error occurred'
    console.error('Error saving team members:', errorMessage)
    showErrorToast({message: `Failed to save team members: ${errorMessage}`})
    throw new Error(errorMessage)
  }
}

/**
 * Check MFA status for all users in the Linear organization.
 * Note: Linear API does not expose MFA status, so mfaEnabled is set to null.
 * @returns A promise resolving to an object containing user details and MFA status.
 */
async function checkUserMFA(accessToken: string): Promise<UserMFAResponse> {
  const validToken = await getValidLinearToken() || accessToken
  const axiosInstance = await createLinearOAuthInstance(validToken)

  const query = `
    query Users {
      users {
        nodes {
          id
          name
          email
        }
      }
    }
  `

  try {
    const response = await axiosInstance.post('', {query})
    const users = response.data.data?.users?.nodes || []

    const results = users.map((user: any) => ({
      id: user.id,
      name: user.name || user.email || `user_${user.id}`,
      email: user.email || '',
      mfaEnabled: null,
    }))

    return {success: true, users: results}
  } catch (error) {
    const errorMessage = getUserFriendlyErrorMessage(error)
    return {success: false, error: errorMessage}
  }
}

const initiateLinearOAuth = () => {
  const clientId = process.env.REACT_APP_LINEAR_CLIENT_ID || ''
  const redirectUri = encodeURIComponent(process.env.REACT_APP_LINEAR_REDIRECT_URI || '')
  const scope = encodeURIComponent('read,write')
  const state =
    Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)

  sessionStorage.setItem('oauth_state', state)
  const linearAuthUrl = `https://linear.app/oauth/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}`
  localStorage.setItem('pendingIntegration', 'Linear')
  window.location.href = linearAuthUrl
}

const getUserFriendlyErrorMessage = (error: unknown): string => {
  const linearError = error as LinearError
  if (linearError.response?.status === 403) {
    return 'Access forbidden. Please check your access token permissions.'
  } else if (linearError.response?.status === 401) {
    return 'Invalid access token. Please reconnect to Linear.'
  } else if (linearError.response?.status === 404) {
    return 'Linear resource not found.'
  } else if (linearError.response?.status === 429) {
    return 'Linear API rate limit exceeded. Please try again later.'
  }
  return (
    linearError.response?.data?.message || linearError.message || 'An unexpected error occurred.'
  )
}

const exchangeCodeForLinearToken = async (
  code: string,
  state: string,
  currentUser: any,
  navigate: any
) => {
  let toastId
  try {
    toastId = showLoadingToast({message: 'Authenticating with Linear...'})
    const response = await fetch(
      `https://us-central1-slate-134a0.cloudfunctions.net/fetchAccessTokenLinear?code=${code}`
    )

    if (!response.ok) {
      throw new Error(`Server returned ${response.status}: ${await response.text()}`)
    }

    const contentType = response.headers.get('content-type')
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Received non-JSON response from server')
    }
    updateLoadingToast(toastId, {
      message: 'Token received, setting up integration...',
      type: 'info',
      isLoading: true,
    })
    const {access_token, refresh_token, expires_in} = await response.json()
    
    const tokenData = {
      access_token,
      refresh_token,
      expires_at: Date.now() + (expires_in * 1000) 
    }
    localStorage.setItem('linear_tokens', JSON.stringify(tokenData))
    localStorage.setItem('linear_access_token', access_token)
    
    const integrationId = await saveLinearIntegration(access_token, refresh_token, state, currentUser)
    dismissLoadingToast(toastId)
    showSuccessToast({message: 'Successfully authenticated with Linear'})
    navigate(`/integrations?id=${integrationId}&service=Linear`, {replace: true})
    return access_token
  } catch (error) {
    showErrorToast({message: 'Linear authentication failed, please try again later'})
    dismissLoadingToast(toastId)
    localStorage.removeItem('linear_access_token')
    localStorage.removeItem('linear_tokens')
    navigate('/integrations', {replace: true})
    throw error
  }
}

const saveLinearIntegration = async (access_token: string, refresh_token: string, state: string, currentUser: any) => {
  try {
    const userMFAResponse = await checkUserMFA(access_token)
    const userMFAResults = userMFAResponse.success ? userMFAResponse.users : []
    const integrationsSnapshot = await db.collection('integrations').get()
    const integrations = integrationsSnapshot.docs.map((doc) => ({
      ...doc.data(),
      id: doc.id,
      name: doc.data().name,
      control_id: doc.data().control_id,
    }))

    const selectedInt = integrations.find((item) => item.name === 'Linear')
    const selectedControl_id = selectedInt?.control_id || []
    let integrationId: string | undefined
    const currentDate = new Date()
    const year = currentDate.getFullYear()
    const month = String(currentDate.getMonth() + 1).padStart(2, '0')
    const day = String(currentDate.getDate()).padStart(2, '0')
    const formattedDate = `${year}-${month}-${day}`
    const todaysDate = `${month}/${day}/${year}`

    const tenantRef = doc(db, 'tenants', currentUser?.tenantId)
    const policyRef = collection(tenantRef, 'integrations')
    const vendorsRef = collection(tenantRef, 'vendors')
    const newAsset = {
      access_token,
      refresh_token,
      state,
      control_id: selectedControl_id,
      name: 'Linear',
      group: 'Project Management',
      img_path: '/integrations/Linear.svg',
      status: 'Connected',
      connectedAt: formattedDate,
      tenantId: currentUser?.tenantId,
      uid: currentUser?.uid,
      userMFAResults,
      updated_at: todaysDate,
    }

    const docRef = await addDoc(policyRef, newAsset)
    integrationId = docRef.id

    const vendorData = {
      authentication_type: 'OAuth',
      category: 'Project Management',
      created_at: serverTimestamp(),
      due_deligence_status: 'Incomplete',
      impact_assessment: [],
      logo: [
        {
          name: 'linear_logo.png',
          size: 3395,
          url: '/integrations/Linear.svg',
        },
      ],
      owner: currentUser?.userName || '',
      policy_url: 'https://linear.app/privacy',
      reliance: '',
      risk_level: '',
      tenantId: currentUser?.tenantId,
      terms_url: 'https://linear.app/terms',
      two_factor: true,
      uid: currentUser?.uid,
      updated_at: serverTimestamp(),
      vendor_name: 'Linear',
      website_url: 'https://linear.app',
      userMFAResults,
    }

    await addDoc(vendorsRef, vendorData)

    const activitiesRef = collection(tenantRef, 'activities')
    await addDoc(activitiesRef, {
      message: `${currentUser?.userName} has successfully connected the Linear integration.`,
      timestamp: serverTimestamp(),
      tenantId: currentUser?.tenantId,
      uid: currentUser?.uid,
      username: currentUser?.userName,
      action: 'Linear connected',
      target: 'Integrations',
      email: currentUser?.email,
      date: todaysDate,
    })
    return integrationId
  } catch (error) {
    const errorMessage = getUserFriendlyErrorMessage(error)
    console.error('Error saving Linear integration:', errorMessage)
    throw error
  }
}

async function fetchTeamsAndSetState(accessToken: string) {
  const validToken = await getValidLinearToken() || accessToken
  const axiosInstance = await createLinearOAuthInstance(validToken)

  const query = `
    query Teams {
      teams {
        nodes {
          id
            name
          }
        }
      }
    `

  try {
    const response = await axiosInstance.post('', {query})
    const teams = response.data.data.teams.nodes

    if (teams.length > 0) {
      return teams
    } else {
      return null
    }
  } catch (error) {
    return null
  }
}

export {
  createTask,
  initiateLinearOAuth,
  createLinearOAuthInstance,
  exchangeCodeForLinearToken,
  saveLinearIntegration,
  fetchTeamsAndSetState,
  saveTeamMembers,
  refreshLinearAccessToken,
  getValidLinearToken,
}
